import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Grid from "veneer/Grid";
import Stack from "veneer/Stack";

import { VALID_MIMETYPES } from "cortex/ui/src/fleet/constant/mediaLibrary";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { addErrorMessageAction } from "cortex/ui/src/redux-store/messages";
import { uploadUserContentToS3Action } from "cortex/ui/src/redux-store/user-contents/actions";
import FilePicker from "cortex/ui/src/ui/file-input";

import MediaUploadFileForm from "./MediaUploadFileForm";
import { isValidMimeType } from "../utils";

interface PendingFile {
  duration: number | string;
  fname: string;
  name: string;
  rawFile: File;
}

interface PendingFilesMap {
  [fname: string]: PendingFile;
}

interface UploadPayload {
  duration: number;
  file: File;
  filename: string;
  folderId?: number;
  name: string;
}

export interface MediaUploadFormProps {
  folderId?: number;
}

export default function MediaUploadForm({ folderId }: MediaUploadFormProps) {
  const dispatch = useAppDispatch();
  const hasUploadError = useAppSelector(
    (state: any) => state.pages.contentGroupForm.hasUploadError,
  );
  const isUploading = useAppSelector(
    (state: any) => state.pages.contentGroupForm.isUploading,
  );
  const [currentUpload, setCurrentUpload] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFilesMap>({});
  const previousIsUploading = useRef(isUploading);

  useEffect(() => {
    const uploadJustFinished = previousIsUploading.current && !isUploading;

    if (currentUpload && uploadJustFinished) {
      setPendingFiles((previousPendingFiles) => {
        if (hasUploadError) {
          return previousPendingFiles;
        }

        const nextPendingFiles = { ...previousPendingFiles };
        delete nextPendingFiles[currentUpload];
        return nextPendingFiles;
      });

      setCurrentUpload(null);
    }

    previousIsUploading.current = isUploading;
  }, [currentUpload, hasUploadError, isUploading]);

  const handleError = useCallback(
    (message: string) => {
      dispatch(addErrorMessageAction(message));
    },
    [dispatch],
  );

  const handleFileBrowse = useCallback(
    (files: ReadonlyArray<File> | null) => {
      const filesToAdd: PendingFilesMap = {};
      const filesWithInvalidMimetype: string[] = [];

      for (const file of files ?? []) {
        if (isValidMimeType(file.type)) {
          filesToAdd[file.name] = {
            duration: 15,
            fname: file.name,
            name: "",
            rawFile: file,
          };
        } else {
          filesWithInvalidMimetype.push(file.name);
        }
      }

      if (filesWithInvalidMimetype.length > 0) {
        handleError(
          `${filesWithInvalidMimetype.join(", ")} had invalid mimetypes. ` +
            `Mimetypes should be one of ${VALID_MIMETYPES.join(", ")}`,
        );
      }

      setPendingFiles((previousPendingFiles) => ({
        ...previousPendingFiles,
        ...filesToAdd,
      }));
    },
    [handleError],
  );

  const handleRemoveContent = useCallback((fname: string) => {
    setPendingFiles((previousPendingFiles) => {
      const nextPendingFiles = { ...previousPendingFiles };
      delete nextPendingFiles[fname];
      return nextPendingFiles;
    });
  }, []);

  const handleUpdateFile = useCallback(
    (file: PendingFile, field: string, value: string | number) => {
      const { fname } = file;

      setPendingFiles((previousPendingFiles) => ({
        ...previousPendingFiles,
        [fname]: {
          ...previousPendingFiles[fname],
          [field]: value,
        },
      }));
    },
    [],
  );

  const handleUploadContent = useCallback(
    (file: PendingFile) => {
      const durationMs = parseInt(String(file.duration), 10) * 1000;

      setCurrentUpload(file.fname);

      const payload: UploadPayload = {
        duration: durationMs,
        file: file.rawFile,
        filename: file.fname,
        folderId,
        name: file.name,
      };

      dispatch(
        uploadUserContentToS3Action(
          payload as Parameters<typeof uploadUserContentToS3Action>[0],
          "Media uploaded!",
        ),
      );
    },
    [dispatch, folderId],
  );

  const pendingFileList = useMemo(
    () => Object.values(pendingFiles),
    [pendingFiles],
  );

  return (
    <Grid container spacing={2}>
      <Grid item md={4} xs={12}>
        <FilePicker
          accept={VALID_MIMETYPES}
          enableDrop
          multiple
          onChange={handleFileBrowse}
        />
      </Grid>
      <Grid item md={8} xs={12}>
        <Stack gap={2}>
          {pendingFileList.map((file) => (
            <MediaUploadFileForm
              disabled={isUploading}
              file={file}
              key={file.fname}
              onRemove={handleRemoveContent}
              onUpdate={handleUpdateFile}
              onUpload={handleUploadContent}
            />
          ))}
        </Stack>
      </Grid>
    </Grid>
  );
}
